import {computed, ref, shallowRef, watch, type Ref} from 'vue'
import {CalendarDateTime, type DateValue} from '@internationalized/date'
import type {Task, TaskFolder, TaskInstance, TaskWithInstance, Recurring} from '@backend/types'
import {useAppStore} from '@/stores/app'
import {apiStringToDateValue, dateValueToApiString, dateValueToJsDate} from './useDateConversion'

export const durationOptions = [
  {value: '', label: 'options.none'},
  {value: '5', label: 'options.dur_5min'},
  {value: '10', label: 'options.dur_10min'},
  {value: '15', label: 'options.dur_15min'},
  {value: '30', label: 'options.dur_30min'},
  {value: '45', label: 'options.dur_45min'},
  {value: '60', label: 'options.dur_1h'},
  {value: '120', label: 'options.dur_2h'},
  {value: '180', label: 'options.dur_3h'},
  {value: '240', label: 'options.dur_4h'},
  {value: '360', label: 'options.dur_6h'},
  {value: '480', label: 'options.dur_8h'},
] as const

export const energyOptions = [
  {value: '', label: 'options.none'},
  {value: 'high', label: 'options.energy_high'},
  {value: 'medium', label: 'options.energy_medium'},
  {value: 'low', label: 'options.energy_low'},
] as const

export const reminderPresets = [
  {value: '-5 minutes', label: 'options.rem_5min'},
  {value: '-15 minutes', label: 'options.rem_15min'},
  {value: '-30 minutes', label: 'options.rem_30min'},
  {value: '-1 hours', label: 'options.rem_1h'},
  {value: '-2 hours', label: 'options.rem_2h'},
  {value: '-4 hours', label: 'options.rem_4h'},
  {value: '-1 days', label: 'options.rem_1d'},
  {value: '-2 days', label: 'options.rem_2d'},
  {value: '-1 weeks', label: 'options.rem_1w'},
] as const

export const folderOptions: TaskFolder[] = [
  'inbox',
  'next',
  'scheduled',
  'waiting',
  'someday',
  'reference',
  'archived',
  'trash',
]

function isSame(a: string | number | null | undefined, b: string | number | null | undefined): boolean {
  const normalizeEmpty = (v: string | number | null | undefined) => {
    if (v === null || v === undefined || v === '') return null
    return v
  }
  return normalizeEmpty(a) === normalizeEmpty(b)
}

function areTagsEqual(a: string[], b: string[] | undefined): boolean {
  const sortedA = [...a].sort()
  const sortedB = [...(b || [])].sort()
  if (sortedA.length !== sortedB.length) return false
  return sortedA.every((v, i) => v === sortedB[i])
}

function areArraysEqual(a: string[], b: string[] | undefined): boolean {
  const filteredA = a.filter((v) => v.trim())
  const filteredB = (b || []).filter((v) => v.trim())
  if (filteredA.length !== filteredB.length) return false
  return filteredA.every((v, i) => v === filteredB[i])
}

function isSameDateValue(a: DateValue | undefined, b: string | null | undefined): boolean {
  if (!a && !b) return true
  if (!a || !b) return false
  const aDate = dateValueToJsDate(a)
  const bDate = dateValueToJsDate(apiStringToDateValue(b))
  return aDate.getTime() === bDate.getTime()
}

export interface ChangedField {
  field: string
  from: string
  to: string
}

export function useTaskEditForm(task: Ref<TaskWithInstance>, taskInstance: Ref<TaskInstance | null>) {
  const appStore = useAppStore()

  const editTitle = ref('')
  const editNotes = ref('')
  const editDuration = ref('')
  const editEnergy = ref('')
  const editDelegatedTo = ref('')
  const editProjectId = ref<string>('')
  const editFolder = ref<TaskFolder>('inbox')
  const editTags = ref<string[]>([])
  const editDueDate = shallowRef<DateValue | undefined>()
  const editDone = ref(false)
  const editAutoComplete = ref(false)
  const editReminders = ref<string[]>([])
  const editRecurringSchedule = ref('')
  const editRecurringCondition = ref('')
  const editRecurringStartAt = shallowRef<DateValue | undefined>()
  const editRecurringEndAt = shallowRef<DateValue | undefined>()
  const isAddingNewContact = ref(false)
  const newContactName = ref('')
  const isSaving = ref(false)
  const canConfigureAutoComplete = computed(() =>
    Boolean(editDueDate.value || (editRecurringSchedule.value && editRecurringCondition.value))
  )

  watch(canConfigureAutoComplete, (value) => {
    if (!value) {
      editAutoComplete.value = false
    }
  })

  function initializeEditFields() {
    const t = task.value
    editTitle.value = t.title
    editNotes.value = t.notes || ''
    editDuration.value = t.duration?.toString() ?? ''
    editEnergy.value = t.energy ?? ''
    editDelegatedTo.value = t.delegated_to || ''
    editProjectId.value = t.project_id != null ? String(t.project_id) : ''
    editFolder.value = t.folder
    editTags.value = [...(t.tags || [])]
    editAutoComplete.value = t.auto_complete || false
    editReminders.value = [...(t.reminders || [])]
    isAddingNewContact.value = false
    newContactName.value = ''

    // Due date from task instance
    const inst = taskInstance.value
    editDone.value = Boolean(inst?.done)
    if (inst?.due_date) {
      editDueDate.value = apiStringToDateValue(inst.due_date)
    } else {
      editDueDate.value = undefined
    }

    // Recurring
    if (t.recurring) {
      editRecurringSchedule.value = t.recurring.schedule
      editRecurringCondition.value = t.recurring.condition
      editRecurringStartAt.value = t.recurring.start_at ? apiStringToDateValue(t.recurring.start_at) : undefined
      editRecurringEndAt.value = t.recurring.end_at ? apiStringToDateValue(t.recurring.end_at) : undefined
    } else {
      editRecurringSchedule.value = ''
      editRecurringCondition.value = ''
      editRecurringStartAt.value = undefined
      editRecurringEndAt.value = undefined
    }
  }

  function isRecurringChanged(): boolean {
    const task_recurring = task.value.recurring
    if (!task_recurring) {
      return !!(
        editRecurringSchedule.value ||
        editRecurringCondition.value ||
        editRecurringStartAt.value ||
        editRecurringEndAt.value
      )
    }
    return (
      editRecurringSchedule.value !== task_recurring.schedule ||
      editRecurringCondition.value !== task_recurring.condition ||
      !isSameDateValue(editRecurringStartAt.value, task_recurring.start_at) ||
      !isSameDateValue(editRecurringEndAt.value, task_recurring.end_at)
    )
  }

  const changedFields = computed(() => {
    const t = task.value
    const inst = taskInstance.value
    const fields: ChangedField[] = []
    const fmt = (v: any) => (v == null || v === '' ? '(empty)' : String(v))
    if (!isSame(editTitle.value, t.title)) fields.push({field: 'title', from: fmt(t.title), to: fmt(editTitle.value)})
    if (!isSame(editNotes.value, t.notes)) fields.push({field: 'notes', from: fmt(t.notes), to: fmt(editNotes.value)})
    if (!isSame(editDuration.value, t.duration))
      fields.push({field: 'duration', from: fmt(t.duration), to: fmt(editDuration.value)})
    if (!isSame(editEnergy.value, t.energy))
      fields.push({field: 'energy', from: fmt(t.energy), to: fmt(editEnergy.value)})
    if (!isSame(editDelegatedTo.value, t.delegated_to))
      fields.push({field: 'delegated_to', from: fmt(t.delegated_to), to: fmt(editDelegatedTo.value)})
    if (!isSame(editProjectId.value, t.project_id != null ? String(t.project_id) : ''))
      fields.push({field: 'project', from: fmt(t.project_id), to: fmt(editProjectId.value)})
    if (!isSame(editFolder.value, t.folder))
      fields.push({field: 'folder', from: fmt(t.folder), to: fmt(editFolder.value)})
    if (!areTagsEqual(editTags.value, t.tags))
      fields.push({field: 'tags', from: fmt(t.tags?.join(', ')), to: fmt(editTags.value.join(', '))})
    const targetAutoComplete = canConfigureAutoComplete.value ? editAutoComplete.value : false
    if (targetAutoComplete !== Boolean(t.auto_complete)) {
      fields.push({field: 'auto_complete', from: fmt(t.auto_complete), to: fmt(targetAutoComplete)})
    }
    if (isRecurringChanged())
      fields.push({field: 'recurring', from: fmt(t.recurring?.schedule), to: fmt(editRecurringSchedule.value)})
    if (!areArraysEqual(editReminders.value, t.reminders))
      fields.push({field: 'reminders', from: fmt(t.reminders?.join(', ')), to: fmt(editReminders.value.join(', '))})
    if (editDone.value !== Boolean(inst?.done)) {
      fields.push({field: 'done', from: fmt(inst?.done), to: fmt(editDone.value)})
    }
    if (!isSameDateValue(editDueDate.value, inst?.due_date))
      fields.push({
        field: 'due_date',
        from: fmt(inst?.due_date),
        to: fmt(editDueDate.value ? dateValueToApiString(editDueDate.value) : null),
      })
    return fields
  })

  const hasChanges = computed(() => changedFields.value.length > 0)

  function addReminder(value: string) {
    if (value && !editReminders.value.includes(value)) {
      editReminders.value.push(value)
    }
  }

  function removeReminder(index: number) {
    editReminders.value.splice(index, 1)
  }

  function handleNewContact() {
    if (newContactName.value.trim()) {
      editDelegatedTo.value = newContactName.value.trim()
    }
    isAddingNewContact.value = false
    newContactName.value = ''
  }

  async function saveChanges(): Promise<{success: boolean; error?: string}> {
    isSaving.value = true
    try {
      const t = task.value
      const inst = taskInstance.value

      // Handle task instance fields.
      const newDueDate = editDueDate.value ? dateValueToApiString(editDueDate.value) : null
      const isDueDateChanged = !isSameDateValue(editDueDate.value, inst?.due_date)
      const isDoneChanged = editDone.value !== Boolean(inst?.done)
      if (isDueDateChanged || isDoneChanged) {
        const instanceUpdate: Partial<Pick<TaskInstance, 'done' | 'due_date'>> = {}
        if (isDueDateChanged) {
          instanceUpdate.due_date = newDueDate
        }
        if (isDoneChanged) {
          instanceUpdate.done = editDone.value
        }

        if (inst) {
          await appStore.updateTaskInstance(inst.task_instance_id, instanceUpdate)
        } else if (newDueDate || editDone.value) {
          await appStore.createTaskInstance(t.task_id, {
            due_date: newDueDate,
            done: editDone.value,
          })
        }
      }

      // Build task update payload (only changed fields)
      const updatedTask: Partial<Task> = {}

      if (!isSame(editTitle.value, t.title)) {
        updatedTask.title = editTitle.value
      }
      if (!isSame(editNotes.value, t.notes)) {
        updatedTask.notes = editNotes.value
      }
      if (!isSame(editDuration.value, t.duration)) {
        updatedTask.duration = editDuration.value || null
      }
      if (!isSame(editEnergy.value, t.energy)) {
        updatedTask.energy = editEnergy.value || null
      }
      if (!isSame(editDelegatedTo.value, t.delegated_to)) {
        updatedTask.delegated_to = editDelegatedTo.value || undefined
      }
      if (!areTagsEqual(editTags.value, t.tags)) {
        updatedTask.tags = editTags.value
      }
      if (!isSame(editProjectId.value, t.project_id != null ? String(t.project_id) : '')) {
        updatedTask.project_id = editProjectId.value ? Number(editProjectId.value) : null
      }
      if (!isSame(editFolder.value, t.folder)) {
        updatedTask.folder = editFolder.value
      }

      const targetAutoComplete = canConfigureAutoComplete.value ? editAutoComplete.value : false
      if (targetAutoComplete !== Boolean(t.auto_complete)) {
        updatedTask.auto_complete = targetAutoComplete
      }

      // Recurring
      if (isRecurringChanged()) {
        if (editRecurringSchedule.value && editRecurringCondition.value) {
          updatedTask.recurring = {
            schedule: editRecurringSchedule.value,
            condition: editRecurringCondition.value,
            start_at: editRecurringStartAt.value ? dateValueToApiString(editRecurringStartAt.value) : undefined,
            end_at: editRecurringEndAt.value ? dateValueToApiString(editRecurringEndAt.value) : undefined,
          }
        } else {
          updatedTask.recurring = undefined
        }
      }

      // Reminders
      if (!areArraysEqual(editReminders.value, t.reminders)) {
        const nonEmpty = editReminders.value.filter((r) => r.trim())
        updatedTask.reminders = nonEmpty.length > 0 ? nonEmpty : []
      }

      if (Object.keys(updatedTask).length > 0) {
        await appStore.updateTask(t.task_id, updatedTask)
      }

      return {success: true}
    } catch (error) {
      console.error('Failed to save changes:', error)
      return {success: false, error: String(error)}
    } finally {
      isSaving.value = false
    }
  }

  return {
    editTitle,
    editNotes,
    editDuration,
    editEnergy,
    editDelegatedTo,
    editProjectId,
    editFolder,
    editTags,
    editDueDate,
    editDone,
    editAutoComplete,
    canConfigureAutoComplete,
    editReminders,
    editRecurringSchedule,
    editRecurringCondition,
    editRecurringStartAt,
    editRecurringEndAt,
    isAddingNewContact,
    newContactName,
    isSaving,
    hasChanges,
    changedFields,
    initializeEditFields,
    addReminder,
    removeReminder,
    handleNewContact,
    saveChanges,
  }
}
